import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@semsis/ui/Spinner';
import { trackEvent } from '@referee/analytics';
import type { Question } from '../model/Question';
import { TestDataManager, loadAvailableLanguages } from '../model/TestDataManager';
import type { ITestResponse } from '../model/types';
import type { XpAwardResult } from '../model/xp-types';
import { getLevelForXp } from '../model/xp-constants';
import { getScreenshotScenario, type ScreenshotScenario } from '../screenshot/scenario';

// Resolved once per session: null on the web and on any invalid payload, so
// the D-12 screenshot scenario never affects normal usage (see
// src/screenshot/scenario.ts).
const screenshotScenario = getScreenshotScenario();

export interface TestDataContextValue {
  question?: Question;
  asked: number;
  correct: number;
  checked: string[];
  reveal: boolean;
  data: Record<string, Question>;
  languageMissing: boolean;
  availableLanguages: string[];
  version: string;
  availableVersions: string[];
  mastered: number;
  dueCount: number;
  boxDistribution: number[];
  globalStreak: number;
  xp: number;
  level: number;
  levelTitle: string;
  xpForNextLevel: number | null;
  xpProgress: number;
  dailyStreak: number;
  lastXpGain: XpAwardResult | null;
  screenshotScenario: ScreenshotScenario | null;
  setVersion: (version: string) => void;
  checkAnswers: (options: string[]) => Promise<ITestResponse>;
  recordAnswer: (questionId: string, answers: string[]) => Promise<ITestResponse>;
  nextQuestion: () => void;
  resetStats: () => Promise<void>;
  resetXp: () => Promise<void>;
}

const TestDataCtx = createContext<TestDataContextValue | null>(null);

export function useTestData(): TestDataContextValue {
  const ctx = useContext(TestDataCtx);
  if (!ctx) {
    throw new Error('useTestData must be used within a TestDataProvider');
  }
  return ctx;
}

export function TestDataProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState<Question>();
  const [asked, setAsked] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [checked, setChecked] = useState<string[]>([]);
  const [reveal, setReveal] = useState(false);
  const [version, setVersion] = useState<string>(screenshotScenario?.catalog ?? 'ihf_07_2025');
  const [availableVersions, setAvailableVersions] = useState<string[]>([]);
  const [languageMissing, setLanguageMissing] = useState(false);
  const [mastered, setMastered] = useState(0);
  const [dueCount, setDueCount] = useState(0);
  const [boxDistribution, setBoxDistribution] = useState<number[]>([0, 0, 0, 0, 0]);
  const [globalStreak, setGlobalStreak] = useState(0);
  const [xp, setXp] = useState(0);
  const [dailyStreak, setDailyStreak] = useState(0);
  const [lastXpGain, setLastXpGain] = useState<XpAwardResult | null>(null);
  const manager = useRef<TestDataManager>(null!);
  if (!manager.current) {
    manager.current = new TestDataManager();
  }
  const scenarioFeedbackApplied = useRef(false);
  const { i18n } = useTranslation();

  useEffect(() => {
    loadAvailableLanguages().then((langs) => {
      setAvailableVersions(Object.keys(langs));
    });
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        let loadedQuestion = await manager.current.initialize(i18n.language, version);
        if (screenshotScenario) {
          loadedQuestion = manager.current.showQuestion(screenshotScenario.questionId) ?? loadedQuestion;
        }
        setQuestion(loadedQuestion);
        setAsked(manager.current.asked);
        setCorrect(manager.current.correct);
        setLanguageMissing(manager.current.languageMissing);
        setMastered(manager.current.mastered);
        setDueCount(manager.current.dueCount);
        setBoxDistribution(manager.current.boxDistribution);
        setGlobalStreak(manager.current.globalStreak);
        const xpProfile = manager.current.xpManager.getProfile();
        setXp(xpProfile.totalXp);
        setDailyStreak(xpProfile.currentDailyStreak);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [i18n.language, version]);

  const nextQuestion = useCallback(() => {
    setQuestion(manager.current.next());
    setReveal(false);
    setChecked([]);
    setLastXpGain(null);
  }, []);

  const checkAnswers = useCallback(async (options: string[]) => {
    const boxBefore = question?.box ?? 1;

    const result = await manager.current.checkAnswer(options);
    setAsked(manager.current.asked);
    setCorrect(manager.current.correct);
    setChecked(options);
    setReveal(true);
    setMastered(manager.current.mastered);
    setDueCount(manager.current.dueCount);
    setBoxDistribution(manager.current.boxDistribution);
    setGlobalStreak(manager.current.globalStreak);
    if (result.xp) {
      setXp(result.xp.newTotalXp);
      setLastXpGain(result.xp);
      const xpProfile = manager.current.xpManager.getProfile();
      setDailyStreak(xpProfile.currentDailyStreak);
    }

    if (question) {
      trackEvent('question_answered', {
        correct: result.answeredCorrect,
        question_id: question.id,
        rule: question.rule,
        version,
        box_before: boxBefore,
        box_after: question.box,
        xp_gained: result.xp?.totalXpGained ?? 0,
      });

      if (result.xp?.leveledUp) {
        trackEvent('level_up', {
          new_level: result.xp.newLevel,
          previous_level: result.xp.previousLevel,
          total_xp: result.xp.newTotalXp,
        });
      }

      if (result.xp?.events.some((e) => e.type === 'category_mastery')) {
        trackEvent('category_mastered', {
          rule: question.rule,
          version,
        });
      }

      const streakEvent = result.xp?.events.find((e) => e.type === 'daily_streak');
      if (streakEvent) {
        trackEvent('daily_streak_bonus', {
          streak_count: streakEvent.metadata?.streakCount,
          xp_bonus: streakEvent.xp,
        });
      }
    }

    return result;
  }, [question, version]);

  // D-12 screenshot scenario: the 'feedback' screen is the 'question' screen
  // already checked. Runs once, after the scenario question has actually
  // landed in `question` state, mirroring the normal check-answer button
  // press via the same `checkAnswers` path used everywhere else.
  useEffect(() => {
    if (
      screenshotScenario?.state === 'feedback'
      && !scenarioFeedbackApplied.current
      && question?.id === screenshotScenario.questionId
    ) {
      scenarioFeedbackApplied.current = true;
      void checkAnswers(screenshotScenario.answers);
    }
  }, [question, checkAnswers]);

  const recordAnswer = useCallback(async (questionId: string, answers: string[]) => {
    const result = await manager.current.recordAnswer(questionId, answers);

    setAsked(manager.current.asked);
    setCorrect(manager.current.correct);
    setMastered(manager.current.mastered);
    setDueCount(manager.current.dueCount);
    setBoxDistribution(manager.current.boxDistribution);
    setGlobalStreak(manager.current.globalStreak);

    if (result.xp) {
      setXp(result.xp.newTotalXp);
      setLastXpGain(result.xp);
      const xpProfile = manager.current.xpManager.getProfile();
      setDailyStreak(xpProfile.currentDailyStreak);
    }

    return result;
  }, []);

  const resetStats = useCallback(async () => {
    const totalAsked = manager.current.asked;
    const totalCorrect = manager.current.correct;
    await manager.current.reset();
    setAsked(manager.current.asked);
    setCorrect(manager.current.correct);
    setMastered(manager.current.mastered);
    setDueCount(manager.current.dueCount);
    setBoxDistribution(manager.current.boxDistribution);
    setGlobalStreak(manager.current.globalStreak);
    trackEvent('stats_reset', { total_asked: totalAsked, total_correct: totalCorrect });
  }, []);

  const resetXp = useCallback(async () => {
    const xpProfile = manager.current.xpManager.getProfile();
    const xpLost = xpProfile.totalXp;
    const levelLost = getLevelForXp(xpLost).level;
    await manager.current.resetXp();
    setXp(0);
    setDailyStreak(0);
    setLastXpGain(null);
    trackEvent('xp_reset', { xp_lost: xpLost, level_lost: levelLost });
  }, []);

  const levelInfo = useMemo(() => getLevelForXp(xp), [xp]);

  const value = useMemo<TestDataContextValue>(
    () => ({
      question,
      asked,
      correct,
      checked,
      reveal,
      data: manager.current.data,
      languageMissing,
      availableLanguages: manager.current.availableLanguages,
      version,
      availableVersions,
      mastered,
      dueCount,
      boxDistribution,
      globalStreak,
      xp,
      level: levelInfo.level,
      levelTitle: levelInfo.titleKey,
      xpForNextLevel: levelInfo.xpForNextLevel,
      xpProgress: levelInfo.progress,
      dailyStreak,
      lastXpGain,
      screenshotScenario,
      setVersion,
      checkAnswers,
      recordAnswer,
      nextQuestion,
      resetStats,
      resetXp,
    }),
    [question, asked, correct, checked, reveal, languageMissing, version, availableVersions, mastered, dueCount, boxDistribution, globalStreak, xp, levelInfo, dailyStreak, lastXpGain, checkAnswers, recordAnswer, nextQuestion, resetStats, resetXp],
  );

  if (loading) {
    return <Spinner page />;
  }

  return <TestDataCtx value={value}>{children}</TestDataCtx>;
}

